import { type ContractSourceMeta, createSyntheticMeta } from '@/contracts/questionnaire'
import type { ApiEnvelope } from '@/types/api'
import { isApiDataClientMode, shouldAllowApiModeMockFallback } from './mode'

type ApiMeta = NonNullable<ApiEnvelope<unknown>['meta']>

export class DataClientRequestError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'DataClientRequestError'
  }
}

export function buildQuery(params: Record<string, unknown>) {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    query.set(key, String(value))
  }
  const serialized = query.toString()
  return serialized ? `?${serialized}` : ''
}

export function toSourceMeta(meta: ApiMeta | undefined): ContractSourceMeta {
  if (!meta) return createSyntheticMeta()
  const source = meta.source === 'database' ? 'database' : 'synthetic'
  const fallback = source === 'database' ? 'database' : 'mock-local'
  return {
    source,
    permissionScope:
      typeof meta.permission_scope === 'string' && meta.permission_scope.trim()
        ? meta.permission_scope
        : fallback,
    dataDate: typeof meta.data_date === 'string' ? meta.data_date : createSyntheticMeta().dataDate,
    confidence: typeof meta.confidence === 'number' ? meta.confidence : 1,
  }
}

export function metaWithSource(meta: ApiMeta | undefined) {
  return {
    ...toSourceMeta(meta),
    total: typeof meta?.total === 'number' ? meta.total : 0,
    page: typeof meta?.page === 'number' ? meta.page : 1,
    limit: typeof meta?.limit === 'number' ? meta.limit : 20,
  }
}

export async function requestApiEnvelope<T>(
  path: string,
  init: RequestInit = {},
): Promise<ApiEnvelope<T>> {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json')

  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers,
  })
  const body = (await response.json().catch(() => null)) as
    | (ApiEnvelope<T> & { code?: string; details?: unknown })
    | null

  if (!response.ok) {
    throw new DataClientRequestError(
      response.status,
      body?.error ?? `API request failed: ${response.status}`,
      body?.code,
      body?.details,
    )
  }

  if (!body || !('data' in body)) {
    throw new DataClientRequestError(502, 'API response is missing ApiEnvelope.data')
  }

  return body
}

export async function apiOrMock<T>({
  label,
  api,
  mock,
}: {
  label: string
  api: () => Promise<T>
  mock: () => Promise<T> | T
}): Promise<T> {
  if (!isApiDataClientMode()) return mock()

  try {
    return await api()
  } catch (error) {
    if (!shouldAllowApiModeMockFallback()) throw error
    const message = error instanceof Error ? error.message : String(error)
    console.warn(`[MiaoForm data-client] ${label} fell back to persistent mock data: ${message}`)
    return mock()
  }
}

export function fireAndForgetApi(label: string, action: () => Promise<unknown>) {
  if (!isApiDataClientMode()) return
  void action().catch((error) => {
    const message = error instanceof Error ? error.message : String(error)
    console.warn(`[MiaoForm data-client] ${label} API sync failed: ${message}`)
  })
}
